<template>
    <div>
        <vue-bottom-sheet-vue2 ref="itemRef"
                               @closed="emitClose"
                               :drag-color="dragThumbColor"
                               class="dish-details-wrap">
            <template #header>
                <div class="dish-details__sticky"
                     :class="{ 'dish-details__sticky--visible': isHeaderStuck }">
                    <div v-if="!!computedDish.image"
                         class="dish-details__sticky-thumb">
                        <VImage :src="computedDish.image"
                                :alt="`Image of ${computedDish.name}`"
                                loading="eager"
                                decoding="sync" />
                    </div>
                    <div class="dish-details__sticky-info">
                        <span class="dish-details__sticky-name">{{ computedDish.name }}</span>
                        <span class="dish-details__sticky-price">{{
                            getFormattedItemPrice(comCalcDishPrice, { isSingle: true, includeSubItems: false })
                        }}</span>
                    </div>
                </div>
            </template>
            <div class="dish-details"
                 ref="dishDetails">

                <div v-if="!!computedDish.image"
                     class="dish-details__image">
                    <VImage :src="computedDish.image"
                            :alt="`Image of ${computedDish.name}`"
                            loading="eager"
                            decoding="sync" />
                    <span class="dish-details__image-scrim"></span>
                </div>

                <IconButtonClose class="close_btn"
                                 :class="{ 'close_btn--floating': !computedDish.image }"
                                 @click="close" />

                <!-- Always reserve bottom space: the .dish-details__order bar is
                     absolutely pinned to the sheet bottom and ALWAYS renders (in WMM
                     only its Add-to-cart button is hidden — the favourite button
                     stays), so without the spacer the bar overlaps the last content
                     with no scroll room and the sheet reads as clipped. -->
                <div class="dish-details__wrap dish-details__wrap__spacer">
                    <div class="dish-details__content">
                        <div class="dish-details__content-title"
                             ref="titleAnchor">
                            <h3>{{ computedDish.name }}</h3>
                            <span v-if="computedDish.rating"
                                  class="dish-details__rating">
                                <span class="dish-details__rating-star">★</span>{{ computedDish.rating }}
                            </span>
                        </div>
                        <p v-if="computedDish.description"
                           class="dish-details__content-text-description"
                           v-html="computedDish.description">
                        </p>
                        <div class="dish-details__content-description">
                            <span v-if="Boolean(computedDish.is_gluten_free)"
                                  class="dish-details__pill dish-details__pill--diet"
                                  @click.stop="showIconDetails('gluten-free')">
                                <IconGluttenFreeOutline width="18"
                                                        height="18" />
                                {{ $t('gluten-free') }}
                            </span>
                            <span v-if="Boolean(computedDish.is_spicy)"
                                  class="dish-details__pill dish-details__pill--diet"
                                  @click.stop="showIconDetails('chili-pepper')">
                                <IconPepperHot width="18"
                                               height="18" />
                                {{ $t('chili-pepper') }}
                            </span>
                            <span v-if="Boolean(computedDish.is_vegetarian)"
                                  class="dish-details__pill dish-details__pill--diet"
                                  @click.stop="showIconDetails('vegeterian')">
                                <IconVegetarian width="18"
                                                height="18" />
                                {{ $t('vegeterian') }}
                            </span>
                            <span v-if="Boolean(computedDish.is_low_calorie)"
                                  class="dish-details__pill dish-details__pill--diet"
                                  @click.stop="showIconDetails('low-calories')">
                                <IconLowCal width="18"
                                            height="18" />
                                {{ $t('low-calories') }}
                            </span>

                            <span v-if="computedDish.weight > 0"
                                  class="dish-details__pill dish-details__pill--nutri">{{ computedDish.weight }} g</span>
                            <span v-if="computedDish.volume > 0"
                                  class="dish-details__pill dish-details__pill--nutri">{{ computedDish.volume }} ml</span>
                            <span v-if="computedDish.alcohol > 0"
                                  class="dish-details__pill dish-details__pill--nutri">{{ computedDish.alcohol }} %</span>
                        </div>
                        <div class="dish-details__content-footer">
                            <span>{{ getFormattedItemPrice(comCalcDishPrice, { isSingle: true, includeSubItems: false })
                            }} </span>
                            <div v-if="orderIsAvailable(computedDish)"
                                 class="dish-details__quantity">
                                <span class="dish-details__quantity-label">{{ $t('Quantity') }}</span>
                                <VItemCounter :id="computedDish.id"
                                              :value="quantity"
                                              @update:value="updateQuantity" />
                            </div>
                        </div>
                    </div>
                    <template v-if="computedDish.options.length > 0 && !isWMM">
                        <div v-for="selectedOptions, index in selectedOptionsArr"
                             :key="`options-for-${index}`"
                             class="dish-details__options">
                            <h3 v-if="index !== 0"
                                class="dish-details__options__title">
                                {{ `${$t('pages.menu.for')} ` }}
                                <span class="dish-details__options__title__count">{{ index + 1 }}</span>
                                {{ ` ${computedDish.name}:` }}
                            </h3>
                            <DishDetailsOptionsSelect v-for="option in computedDish.options"
                                                      :key="option.id"
                                                      :data="option"
                                                      :radio-name="index"
                                                      :id="'option_' + option.id"
                                                      v-model="selectedOptions[option.id]"
                                                      class="dish-details__options__select" />

                        </div>
                    </template>
                </div>
                <div class="dish-details__order">
                    <button type="button"
                            class="dish-details__fav"
                            :class="{ 'is-active': isFavorite, 'dish-details__fav--full': !orderIsAvailable(computedDish) }"
                            :aria-label="isFavorite ? $t('pages.menu.favorites.remove') : $t('pages.menu.favorites.add')"
                            @click="toggleFavorite">
                        <IconStar v-if="isFavorite" />
                        <IconStarOutline v-else />
                        <span class="dish-details__fav-label">{{
                            isFavorite ? $t('pages.menu.favorites.remove') : $t('pages.menu.favorites.add')
                        }}</span>
                    </button>
                    <VButton v-on:click.native="addToCart"
                             v-if="orderIsAvailable(computedDish)"
                             variant="primary"
                             gradient
                             size="lg"
                             fullWidth>
                        {{ $t("Add to cart") }} <span class="dish-details__order__circle"></span> {{
                            getFormattedItemPrice(comCalcDishPrice) }}
                    </VButton>
                </div>

            </div>
        </vue-bottom-sheet-vue2>
        <FoodIconInfoPopup ref="iconDetails"
                           :icon="activeIcon" />
    </div>
</template>

<script>
// import BottomSheet from '@douxcode/vue-spring-bottom-sheet'
// import '@douxcode/vue-spring-bottom-sheet/dist/style.css'
import VueBottomSheetVue2 from "@webzlodimir/vue-bottom-sheet-vue2";
import VItemCounter from "@/components/ui/VItemCounter.vue";
import VButton from "@/components/ui/VButton.vue";
import IconButtonClose from "@/components/icons/IconButtonClose.vue";
import IconGluttenFreeOutline from '@/components/icons/IconGluttenFreeOutline.vue';
import IconPepperHot from '@/components/icons/IconPepperHot.vue';
import IconVegetarian from '@/components/icons/IconVegetarian.vue';
import IconLowCal from '@/components/icons/IconLowCal.vue';
import IconStar from '@/components/icons/IconStar.vue';
import IconStarOutline from '@/components/icons/IconStarOutline.vue';
import DishDetailsOptionsSelect from "./components/DishDetailsOptionsSelect.vue";
import FoodIconInfoPopup from "@/components/FoodIconInfoPopup.vue";
import VImage from "@/components/ui/VImage.vue";
import { getFormattedItemPrice } from "@/utils/itemHelper";
import { lockNav, unlockNav } from "@/utils/navLock";

export default {
    name: "DishDetails",
    components: {
        VueBottomSheetVue2,
        VButton,
        VItemCounter,
        VImage,
        IconButtonClose,
        IconGluttenFreeOutline,
        IconPepperHot,
        IconVegetarian,
        IconLowCal,
        IconStar,
        IconStarOutline,
        DishDetailsOptionsSelect,
        FoodIconInfoPopup,
    },
    props: {
        dishId: { type: [String, Number], default: null },
        dish: { type: [Object], default: null }
    },
    emits: ['input'],
    data() {
        return {
            selectedOptionsArr: [],
            quantity: 1,
            toastOptions: {
                position: "top-center",
                timeout: 3000,
                closeOnClick: true
            },
            bottomSheetStyle: {
                '--custom-height': '',
            },
            activeIcon: null,
            // Sticky mini-header: shown once the dish image/name are scrolled past.
            isHeaderStuck: false,
        };
    },
    mounted() {
        if (this.dish) {
            if (this.dish.subproducts === null || typeof this.dish.subproducts === 'undefined') {
                this.dish.subproducts = [];
            }
        }
        this.selectedOptionsArr.push(this.initSelectedOptions());
        //this.updateHeight();

        // Teleport to <body>: when opened from the Popular row the sheet is rendered
        // inside a horizontal overflow-scroll container, and on iOS Safari that
        // container becomes the containing block for position:fixed descendants —
        // clipping the sheet to the Popular section. Moving it to <body> escapes it.
        document.body.appendChild(this.$el);

        this.$nextTick(this.setupStickyHeader);
    },
    updated() {
        //this.updateHeight();
    },
    beforeDestroy() {
        // Safety: the sheet is v-if-destroyed on close, so release the nav lock here.
        this.releaseNav();
        if (this._scrollEl) {
            this._scrollEl.removeEventListener('scroll', this.onStickyScroll);
            this._scrollEl.removeEventListener('touchstart', this.onSheetTouchStart);
            this._scrollEl.removeEventListener('touchmove', this.onSheetTouchMove);
            this._scrollEl = null;
        }
        // Remove the teleported root from <body> (Vue's own removal targets the
        // current parent, but do it explicitly to avoid any orphan node).
        if (this.$el && this.$el.parentNode) {
            this.$el.parentNode.removeChild(this.$el);
        }
    },
    watch: {
        quantity: {
            handler(newVal) {
                // Disable each order different options select, dublicate options for quantity 
                // const oldVal = this.selectedOptionsArr.length;
                // if (newVal > oldVal) {
                //     for (let i = 0; i < newVal - oldVal; i++) {
                //         this.selectedOptionsArr.push(this.initSelectedOptions());
                //     }
                // } else if (newVal < oldVal) {
                //     this.selectedOptionsArr.splice(newVal);
                // }
                this.dish.count = newVal;
                this.handleUpdateSubproductsPrice();
            },
            immediate: true,
        },
        selectedOptionsArr: {
            handler() {
                this.handleUpdateSubproductsPrice();
            },
            deep: true
        }
    },
    computed: {
        computedDish() {
            return this.dish ? this.dish : this.$store.getters['menuStore/getDish'];
        },
        isFormValid() {
            return this.dish.options.every(option => {
                if (option.is_required) {
                    return this.selectedOptionsArr.every(selectedOptions => {
                        const values = selectedOptions[option.id];
                        if (Array.isArray(values)) {
                            return values.length > 0;
                        }
                        return values !== null && typeof values !== 'undefined';
                    });
                }
                return true;
            });
        },
        comCalcDishPrice() {
            return { ...this.computedDish };
        },
        isWMM() {
            return this.$store.getters['getIsModeWMM'];
        },
        isFavorite() {
            return this.computedDish
                ? this.$store.getters['favoritesStore/isFavorite'](this.computedDish.id)
                : false;
        },
        dragThumbColor() {
            const onDark = 'rgba(255, 255, 255, 0.85)';   // light pill for dark surfaces
            const onLight = 'rgba(20, 16, 26, 0.32)';      // dark pill for light surfaces
            const isDark = typeof document !== 'undefined'
                && document.documentElement.getAttribute('data-theme') === 'dark';
            // Dark theme: the photo and the frosted header are both dark → light pill.
            if (isDark) return onDark;
            // Light theme: the pill sits over the (dark, scrimmed) photo until the
            // sticky header takes over; over the light header — or a photoless top —
            // a white pill is invisible, so switch to a dark one.
            return (this.isHeaderStuck || !this.computedDish.image) ? onLight : onDark;
        }
    },
    methods: {
        updateHeight() {
            this.$nextTick(() => {
                const content = this.$refs.dishDetails;
                const height = content.scrollHeight;
                this.bottomSheetStyle['--custom-height'] = `${height}px`;
            });
        },
        initSelectedOptions() {
            const res = {};
            this.dish.options.map(option => {
                this.$set(res, option.id, []);
            });
            return res;
        },
        flattenValues(selectedOptions) {
            const flattenedValues = [];
            Object.keys(selectedOptions).forEach((id) => {
                const selectedValues = selectedOptions[id];
                if (Array.isArray(selectedValues)) {
                    selectedValues.forEach((value) => {
                        flattenedValues.push({ ...value, product: { ...value.product, category_id: id } });
                    });
                } else {
                    flattenedValues.push({ ...selectedValues, product: { ...selectedValues.product, category_id: id } });
                }
            });
            return flattenedValues;
        },

        unflattenValues(selectedOptions, flattenedValues) {
            flattenedValues.forEach((item) => {
                const category_id = item.product.category_id;
                selectedOptions[category_id].push(item);
            });
        },
        handleUpdateSubproductsPrice() {
            this.comCalcDishPrice.count = this.quantity;
            if (this.selectedOptionsArr[0]) {
                this.comCalcDishPrice.subproducts = this.flattenValues(this.selectedOptionsArr[0]);
            }
            // for (let i = 0; i < this.quantity; i++) {
            //     this.comCalcDishPrice.subproducts = this.comCalcDishPrice.subproducts.concat(this.flattenValues(this.selectedOptionsArr[i]));
            // }
        },
        updateQuantity(val) {
            this.quantity = val;
        },
        addToCart() {
            if (!this.validateForm()) {
                return;
            }

            // for (let i = 0; i < this.quantity; i++) {
            // }
            const item = { ...this.computedDish };
            item.subproducts = this.flattenValues(this.selectedOptionsArr[0]);
            //item.count = 1;
            item.product = { ...item };

            if (this.isWMM) {
                this.$store.dispatch('cartStore/SET_ITEM_TO_CART', item).then(resp => {
                    if (resp) {
                        this.$toast.success(this.$t('Added to cart'), this.toastOptions);
                    }
                });
            } else {
                this.$store.commit('cartStore/ADD_TO_CART', item);
                this.$toast.success(this.$t('Added to cart'), this.toastOptions);
            }

            this.close();
        },
        toggleFavorite() {
            if (!this.computedDish) return;
            // Suppress the toast on the main menu (it overlaps the header actions);
            // the star's filled state is the feedback there.
            const onMainMenu = this.$route.name === 'menu';
            if (this.isFavorite) {
                this.$store.commit('favoritesStore/REMOVE_FAVORITE', this.computedDish.id);
                if (!onMainMenu) {
                    this.$toast.success(this.$t('pages.menu.favorites.removed'), this.toastOptions);
                }
            } else {
                this.$store.commit('favoritesStore/ADD_FAVORITE', { ...this.computedDish });
                if (!onMainMenu) {
                    this.$toast.success(this.$t('pages.menu.favorites.added'), this.toastOptions);
                }
            }
        },
        orderIsAvailable(computedDish) {
            var locale = this.$store.getters['getCompanyData'].locale;
            // var tableData = this.$store.getters['getTableData'];
            // var table = tableData ? tableData.id : false;
            var available_for_order = computedDish !== null && computedDish.available_for_order;

            return available_for_order
                && (locale.activePaymentCredentials || this.$store.getters['getIsModeHOT'] || this.$store.getters['getIsModeSOT'])
                // && ((locale.pos_terminal_enabled && !locale.posConnection) || locale.posConnection)
                && locale.pos_terminal_enabled
                // && locale.posConnection
                && (this.$store.getters['getCartEnabled']);
            // && table;
        },
        getFormattedItemPrice(item, objParams) {
            return getFormattedItemPrice(item, objParams);
        },
        open() {
            this._navLocked = true;
            this.isHeaderStuck = false;
            lockNav();
            setTimeout(() => this.$refs.itemRef.open(), 10);
        },
        setupStickyHeader() {
            // The sheet's slot content lives inside `.bottom-sheet__main` — the
            // element that actually scrolls. Watch it to reveal the mini-header
            // once the dish image/name have scrolled out of view.
            this._scrollEl = this.$el.querySelector('.bottom-sheet__main');
            if (this._scrollEl) {
                this._scrollEl.addEventListener('scroll', this.onStickyScroll, { passive: true });
                // iOS Safari: when the content is taller than the sheet (a dish with
                // options), pulling down from the very top starts the rubber-band
                // overscroll and scrollTop goes NEGATIVE. The sheet library only
                // lets a content drag move/close the sheet while scrollTop === 0,
                // so the swipe-down-to-close gesture dies over the photo/content
                // and only the drag thumb works. Cancelling the native overscroll
                // here pins scrollTop at 0, letting the library's own pan handler
                // take over the pull and close the sheet.
                this._scrollEl.addEventListener('touchstart', this.onSheetTouchStart, { passive: true });
                this._scrollEl.addEventListener('touchmove', this.onSheetTouchMove, { passive: false });
            }
        },
        onSheetTouchStart(event) {
            this._touchStartY = event.touches[0].clientY;
        },
        onSheetTouchMove(event) {
            if (typeof this._touchStartY !== 'number' || !this._scrollEl) return;
            const pullingDown = event.touches[0].clientY > this._touchStartY;
            if (pullingDown && this._scrollEl.scrollTop <= 0 && event.cancelable) {
                event.preventDefault();
            }
        },
        onStickyScroll() {
            const scrollEl = this._scrollEl;
            const titleEl = this.$refs.titleAnchor;
            if (!scrollEl || !titleEl) return;
            // Stick as soon as the dish name's bottom crosses the top of the
            // scroll viewport (getBoundingClientRect handles image / no-image).
            const viewportTop = scrollEl.getBoundingClientRect().top;
            const titleBottom = titleEl.getBoundingClientRect().bottom;
            this.isHeaderStuck = titleBottom <= viewportTop + 8;
        },
        releaseNav() {
            // Bring the nav back as the sheet STARTS closing, so its fade-in
            // overlaps the slide-down instead of popping in afterwards. Guarded so
            // it fires once regardless of which close path runs.
            if (this._navLocked) {
                this._navLocked = false;
                unlockNav();
            }
        },
        close() {
            this.releaseNav();
            this.$refs.itemRef.close();
        },
        emitClose() {
            this.releaseNav();
            // The sheet library inlines `overflow-y: auto` on <html> right before
            // emitting 'closed' (and 'hidden' while open/dragging). Any explicit
            // overflow left on the root stops body's overflow-x from propagating
            // to the viewport, so BODY becomes the scroll container: window scroll
            // events die (menu header stops collapsing) and iOS Safari pins its
            // toolbars open — the grey bottom bar returns for good. Restore the
            // stylesheet-driven values now that the lib has finished writing.
            document.documentElement.style.overflowY = '';
            document.documentElement.style.overscrollBehavior = '';
            setTimeout(() => {
                this.$emit('input', false);
            }, 500);
        },
        showIconDetails(icon) {
            this.activeIcon = icon;
            this.$nextTick(() => {
                this.$refs.iconDetails.open();
            });
        },
        validateForm() {
            // Find the first required option that hasn't been chosen.
            const missing = this.dish.options.find(option => {
                if (!option.is_required) return false;
                const isChecked = this.selectedOptionsArr.every(selectedOptions => {
                    const values = selectedOptions[option.id];
                    if (Array.isArray(values)) {
                        return values.length > 0;
                    }
                    return values !== null && typeof values !== 'undefined';
                });
                return !isChecked;
            });

            if (missing) {
                // Scroll to it AND tell the user what's missing (was silent before).
                this.scrollInto('option_' + missing.id);
                this.$toast.warning(
                    this.$t('pages.menu.selectRequired', { name: missing.name }),
                    this.toastOptions
                );
                return false;
            }
            return true;
        },
        scrollInto(id) {
            this.$nextTick(() => {
                const element = document.getElementById(id);

                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            });
        },
    }
};
</script>

<style lang="scss" scoped>
small,
h3,
p {
    margin: 0;
    padding: 0;
}

.dish-details-wrap {
    /* NOTE: the library renders its `.bottom-sheet` root ON the component host
       element, so our `dish-details-wrap` class sits on the SAME node — target
       it with a compound selector (&.bottom-sheet), never a descendant one. */

    /* The library's root has no z-index, so the floating nav (z-index 20) and
       later page content paint over it — and a sheet opened from the Popular
       row appeared trapped in that section. Lift it above everything (below
       toasts at 9999). */
    &.bottom-sheet {
        z-index: 2000;
    }

    /* Skirt: a sheet-coloured slab painted BELOW the viewport bottom while the
       sheet is open. iOS Safari's minimized address pill floats over a blur of
       whatever is rendered under the screen edge — without this it sampled
       around the fixed sheet and painted a grey strip under the popup. Lives on
       the fixed root (the card itself clips overflow). No open/closed guard
       needed: DishDetails is v-if-mounted only while the sheet is open. */
    &.bottom-sheet::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        height: 160px;
        background: var(--surface-card);
        pointer-events: none;
    }

    /* Backdrop — darker + frosted so the page reads as pushed back behind the
       sheet. Colour is the theme-aware scrim (0.45 light / 0.62 dark, vs the
       library's flat 30% black); the blur needs the semi-transparent fill to
       show through. Overrides the inline `overlay-color` prop, hence !important. */
    ::v-deep .bottom-sheet__overlay {
        background: var(--overlay-scrim) !important;
        backdrop-filter: blur(6px);
        -webkit-backdrop-filter: blur(6px);
    }

    /* Sheet shell — round top to ~32px and keep the scroll behaviour intact. */
    &::v-deep .bottom-sheet__card {
        border-radius: 32px 32px 0 0 !important;
        background: var(--surface-card) !important;
    }

    ::v-deep .bottom-sheet__content {
        max-height: 90vh !important;
        border-radius: 32px 32px 0 0 !important;
        background: var(--surface-card) !important;
    }

    /* Refined drag handle: small 42×5 pill, translucent white, centred near top. */
    ::v-deep .bottom-sheet__draggable-area {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 26px;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        z-index: 12;
    }

    /* Background colour comes from the component's `drag-color` prop (inline
       style would otherwise win over CSS); we only refine the pill geometry. */
    ::v-deep .bottom-sheet__draggable-thumb {
        width: 42px !important;
        height: 5px !important;
        border-radius: var(--radius-pill) !important;
        margin: 0 auto !important;
    }

    ::v-deep .bottom-sheet__main {
        /* Round the SCROLL container itself (not just the outer card). The card's
           overflow:hidden clip doesn't reliably contain an inner scroller on
           iOS/Chrome, so as content scrolled up the square top corners reappeared
           and poked through the sticky header's rounded corners. Clipping here
           keeps the top rounded at every scroll position and leaves nothing square
           behind the header's corners. */
        border-radius: 32px 32px 0 0;

        /* Keep the sheet's inner scroll from chaining to the page behind it and
           damp the iOS edge bounce (the JS touchmove guard handles the top edge
           where the bounce broke swipe-to-close). */
        overscroll-behavior-y: contain;

        &::-webkit-scrollbar {
            display: none;
        }
    }
}

/* Sticky mini-header — lives in the sheet's `header` slot. `.bottom-sheet__content`
   carries a transform, so it's the containing block: top:0 pins us to the card top,
   overlaying the scroll area (no layout shift). Hidden until the name scrolls past. */
.dish-details__sticky {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 11;

    display: flex;
    align-items: center;
    gap: 12px;

    /* top pad clears the 26px drag handle; right pad clears the floating close btn */
    padding: 22px 60px 12px 16px;

    background: var(--header-blur-bg);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-bottom: 1px solid var(--border-subtle);
    border-radius: 32px 32px 0 0;

    opacity: 0;
    transform: translateY(-8px);
    pointer-events: none;
    transition: opacity var(--dur-fast) var(--ease-standard),
                transform var(--dur-fast) var(--ease-standard);

    &--visible {
        opacity: 1;
        transform: translateY(0);
        pointer-events: auto;
    }

    &-thumb {
        flex-shrink: 0;
        width: 44px;
        height: 44px;
        border-radius: var(--radius-lg);
        overflow: hidden;

        &>img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
    }

    &-info {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
    }

    &-name {
        font-family: var(--font-sans);
        font-size: 16px;
        font-weight: var(--weight-bold);
        line-height: 1.2;
        color: var(--text-primary);

        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &-price {
        font-family: var(--font-sans);
        font-size: 14px;
        font-weight: var(--weight-semibold);
        line-height: 1.2;
        color: var(--text-price);
    }
}

.dish-details {

    .close_btn {
        position: absolute;
        right: 16px;
        top: 16px;
        z-index: 13;
        width: 34px;
        height: 34px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: var(--radius-full);
        background: rgba(20, 16, 26, 0.45);
        backdrop-filter: blur(6px);
        cursor: pointer;
        transition: transform var(--dur-fast) var(--ease-spring),
                    background var(--dur-fast) var(--ease-standard);

        &:active { transform: scale(0.9); }

        /* No photo → put the close button on a solid chip so it stays visible. */
        &--floating {
            background: var(--surface-subtle);
        }
    }

    &__image {
        position: relative;
        overflow: hidden;
        width: 100%;
        height: 248px;

        border-radius: 32px 32px 0 0;

        &>img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        &-scrim {
            position: absolute;
            inset: 0 0 auto 0;
            height: 96px;
            pointer-events: none;
            background: linear-gradient(180deg, rgba(20, 16, 26, 0.35) 0%, rgba(20, 16, 26, 0) 100%);
        }
    }

    &__wrap {
        display: grid;
        gap: 14px;

        &__spacer {
            padding-bottom: 90px;

            @media only screen and (max-width: 768px) {
                padding-bottom: 130px;
            }
        }
    }

    &__content {
        padding: 22px 20px;

        display: flex;
        flex-direction: column;
        gap: 14px;

        background-color: var(--surface-card);

        &-title {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 12px;

            &>h3 {
                font-family: var(--font-sans);
                font-size: 25px;
                font-weight: var(--weight-black);
                line-height: 1.15;
                letter-spacing: -0.5px;
                color: var(--text-primary);
            }
        }

        &-text-description {
            font-family: var(--font-sans);
            font-weight: var(--weight-medium);
            font-size: 15px;
            line-height: 1.45;
            white-space: pre-line;
            color: var(--text-secondary);
        }

        &-description {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 8px;
        }

        &-footer {
            display: flex;
            justify-content: space-between;
            align-items: center;

            &>span {
                font-family: var(--font-sans);
                font-size: var(--text-2xl);
                font-weight: var(--weight-bold);
                line-height: 1.2;
                letter-spacing: 0.1px;
                color: var(--text-price);
            }
        }
    }

    &__rating {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        gap: 4px;

        padding: 4px 9px;
        border-radius: var(--radius-pill);
        background: var(--warning-soft);

        font-family: var(--font-sans);
        font-size: var(--text-sm);
        font-weight: var(--weight-bold);
        color: #B5611A;

        &-star {
            font-size: var(--text-sm);
            line-height: 1;
            color: #B5611A;
        }
    }

    &__pill {
        display: inline-flex;
        align-items: center;
        gap: 6px;

        padding: 6px 12px;
        border-radius: var(--radius-pill);

        font-family: var(--font-sans);
        font-size: var(--text-sm);
        font-weight: var(--weight-semibold);
        line-height: 1;
        text-transform: capitalize;
        cursor: default;

        &--diet {
            background: var(--color-primary-soft);
            color: var(--color-primary);
            cursor: pointer;

            // Brand text is hard to read on the soft chip in dark mode — use white.
            :root[data-theme="dark"] & {
                color: #fff;
            }
        }

        &--nutri {
            background: var(--surface-subtle);
            color: var(--text-secondary);
        }
    }

    &__quantity {
        display: flex;
        align-items: center;
        gap: 12px;

        &-label {
            font-family: var(--font-sans);
            font-size: var(--text-xs);
            font-weight: var(--weight-bold);
            letter-spacing: 0.05em;
            text-transform: uppercase;
            color: var(--text-muted);
        }
    }

    &__order {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;

        display: flex;
        align-items: center;
        gap: 10px;

        padding: 14px 16px calc(14px + env(safe-area-inset-bottom, 0px));

        /* Solid sheet colour (not the translucent header blur): the bar must read
           as one piece with the sheet body behind/below it, in both themes. */
        background: var(--surface-card);
        border-top: 1px solid var(--border-subtle);

        /* The bar is short and its bottom edge is the screen edge — the default
           CTA glow (12px offset / 28px blur) got cut mid-bloom by that edge and
           read as an artifact. Keep the glow tight so it fades out inside the bar. */
        ::v-deep .btn--gradient {
            box-shadow: 0 4px 12px var(--sm-glow);
        }

        &__circle {
            width: 5px;
            height: 5px;
            margin: 0 2px;

            border-radius: var(--radius-full);
            display: inline-block;
            vertical-align: middle;

            background-color: rgba(255, 255, 255, 0.7);
        }
    }

    &__fav {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;

        width: 60px;
        height: 60px;

        border: 1px solid var(--border-subtle);
        border-radius: var(--radius-xl);

        background: var(--surface-card);
        color: var(--text-muted);
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;

        transition: transform var(--dur-fast) var(--ease-standard),
                    color var(--dur-fast) var(--ease-standard),
                    border-color var(--dur-fast) var(--ease-standard);

        &:active { transform: scale(0.92); }

        &.is-active {
            border-color: var(--fav-star);
            color: var(--fav-star);
        }

        /* Text label beside the star — hidden in the compact (icon-only) state,
           revealed when the button goes full width so the lone control reads as a
           proper "Add to favorites" action. */
        &-label {
            display: none;
            font-family: var(--font-sans);
            font-size: 16px;
            font-weight: var(--weight-bold);
            white-space: nowrap;
        }

        /* No order button (WMM / non-orderable dish) → the star is the only control
           in the bar, so let it fill the full width instead of sitting as a lone
           square on the left. flex:1 overrides the base 60px width and shrink:0. */
        &--full {
            flex: 1;
        }

        &--full &-label {
            display: inline;
        }
    }

    &__options {
        display: grid;
        gap: 12px;

        padding: 0 20px;

        &__title {
            padding: 0 2px;
            font-family: var(--font-sans);
            font-weight: var(--weight-bold);
            color: var(--text-primary);

            &__count {
                font-weight: inherit;
                color: var(--color-primary);
            }
        }
    }
}
</style>
